import {type SiteConcession} from 'client';

import {useGetSiteConcessions} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {useEffect, useState, type ChangeEvent} from 'react';
import {Divider, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {type AxiosError} from 'axios';
import Alert from 'helpers/Alert';
import {NumericField} from 'components/Generic/NumericField';
import {mutate} from 'swr';
import {SWRKeyEnum} from 'interfaces';
import {kilosToTons, tonsToKilos} from 'helpers/UnitsFormatHandler';

export const SiteConcessionForm = ({siteId, createUpdateSite}: {siteId: string; createUpdateSite: boolean}) => {
  const {planId} = useParseURL();
  const [concession, setConcession] = useState<SiteConcession>({
    siteId,
    concessionLimit: 0,
    planId,
    id: '',
  });
  const {t} = useTranslation('common');
  const {data: siteConcessions} = useGetSiteConcessions({resourceId: [planId, siteId]});

  const handleChange = (prop: keyof SiteConcession) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(',', '.');
    const numberValue = parseFloat(value);
    concession && setConcession({...concession, [prop]: numberValue});
  };

  useEffect(() => {
    if (siteConcessions && siteConcessions.length > 0) {
      setConcession(siteConcessionReceiver(siteConcessions[0]));
    }
  }, [siteConcessions]);

  useEffect(() => {
    if (createUpdateSite && concession) {
      requestSiteConcession(
        {
          ...concession,
          siteId,
        },
        setConcession,
      );
    }
  }, [createUpdateSite]);

  const onSubmit = () => {};
  return (
    <>
      <Divider />
      <Typography color={'text.secondary'} sx={{marginLeft: 1}}>
        {t('siteConcession')}
      </Typography>

      <NumericField
        {...{handleChange: handleChange('concessionLimit'), onKeyPress: onSubmit}}
        value={concession?.concessionLimit ?? 0}
        fieldName="concessionLimit"
        // helperText="concessionLimitHelperText"
        endAdornment="Tons"
      />
      <Divider />
    </>
  );
};

const requestSiteConcession = (
  siteConcession: SiteConcession,
  setConcession: React.Dispatch<React.SetStateAction<SiteConcession>>,
) => {
  const siteConcessionToSend = siteConcessionSender(siteConcession);
  const request = siteConcession.id
    ? backendCommunicator.editSiteConcession(siteConcession.id, siteConcessionToSend)
    : backendCommunicator.addSiteConcession(siteConcessionToSend);
  request
    .then((response) => {
      response.data && setConcession(siteConcessionReceiver(response.data));
      void mutate([siteConcession.siteId, SWRKeyEnum.GET_SITE_CONCESSIONS]);
      void mutate([siteConcession.planId, SWRKeyEnum.GET_SITE_CONCESSIONS]);
    })
    .catch((error: AxiosError) => {
      Alert.error(error);
    });
};

const siteConcessionSender = (siteConcession: SiteConcession): SiteConcession => {
  return {
    ...siteConcession,
    concessionLimit: tonsToKilos(siteConcession.concessionLimit),
  };
};

const siteConcessionReceiver = (siteConcession: SiteConcession): SiteConcession => {
  return {
    ...siteConcession,
    concessionLimit: kilosToTons(siteConcession.concessionLimit),
  };
};
